import { DashboardEconomyView } from "../home/DashboardEconomyView";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchAccountingSummary } from "../../../api/company";
import {
  downloadAccountingSummaryCalculationGuide,
  fetchAccountingSummaryCalculationGuide,
} from "../../../api/accounting";
import { useTranslation } from "react-i18next";
import { PeriodSelector } from "../hours/period_selector";
import {
  yearFromPeriod,
  yearToPeriod,
} from "./accountingYearPeriod";
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { CostsDetailDialog } from "./CostsDetailDialog";
import { AccountingSummaryDetailDialog } from "./AccountingSummaryDetailDialog";
import { CalculationInfoIcon } from "./CalculationInfoIcon";
import { CalculationGuideDialog } from "./CalculationGuideDialog";
import { SUMMARY_CALC_GUIDE_KEYS } from "./AccountingSummaryCalcGuides";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";
import { isPlatformOpsUser } from "../../platform-broadcast/constants";

export const AccountingSummarySection = ({ dashboardDesign = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isSuperuser = isPlatformOpsUser(currentUser?.email);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [costsDialogOpen, setCostsDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [calcGuideKey, setCalcGuideKey] = useState(null);
  const [guideBusy, setGuideBusy] = useState(false);
  const [guideMessage, setGuideMessage] = useState(null);

  const period = useMemo(() => yearToPeriod(selectedYear), [selectedYear]);

  const handlePeriodChange = useCallback((nextPeriod) => {
    setSelectedYear(yearFromPeriod(nextPeriod));
  }, []);

  const openCalcGuide = useCallback((key) => {
    if (SUMMARY_CALC_GUIDE_KEYS[key]) setCalcGuideKey(key);
  }, []);

  const handleDownloadCalcGuide = useCallback(async () => {
    setGuideBusy(true);
    setGuideMessage(null);
    try {
      await downloadAccountingSummaryCalculationGuide({
        year: selectedYear,
        filename: `accounting-summary-guide-${selectedYear}.json`,
      });
      setGuideMessage(t("accounting.summary.calc_guide.json.downloaded"));
    } catch (err) {
      setGuideMessage(err.message || t("accounting.summary.calc_guide.json.error"));
    } finally {
      setGuideBusy(false);
    }
  }, [selectedYear, t]);

  const handleCopyCalcGuide = useCallback(async () => {
    setGuideBusy(true);
    setGuideMessage(null);
    try {
      const data = await fetchAccountingSummaryCalculationGuide({ year: selectedYear });
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setGuideMessage(t("accounting.summary.calc_guide.json.copied"));
    } catch (err) {
      setGuideMessage(err.message || t("accounting.summary.calc_guide.json.error"));
    } finally {
      setGuideBusy(false);
    }
  }, [selectedYear, t]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSummary(null);
    fetchAccountingSummary(selectedYear).then((result) => {
      if (cancelled) return;
      if (result.error || !result.data) setError(t("accounting.summary.load_error"));
      else setSummary(result.data);
    }).catch(() => {
      if (!cancelled) setError(t("accounting.summary.load_error"));
    }).finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedYear, retry, t]);

  // Prepare monthly data for recharts
  const prepareMonthlyChartData = () => {
    if (!summary?.monthly) return [];
    
    const monthNames = [
      t("accounting.summary.months.january"),
      t("accounting.summary.months.february"),
      t("accounting.summary.months.march"),
      t("accounting.summary.months.april"),
      t("accounting.summary.months.may"),
      t("accounting.summary.months.june"),
      t("accounting.summary.months.july"),
      t("accounting.summary.months.august"),
      t("accounting.summary.months.september"),
      t("accounting.summary.months.october"),
      t("accounting.summary.months.november"),
      t("accounting.summary.months.december"),
    ];
    
    const chartData = [];
    
    for (let month = 1; month <= 12; month++) {
      const monthData = summary.monthly[month];
      if (monthData) {
        const income = parseFloat(monthData.income?.total || 0);
        const cost = parseFloat(monthData.costs?.total || 0);
        const result = parseFloat(monthData.profit || 0);
        
        chartData.push({
          month: monthNames[month - 1],
          income: income,
          cost: cost,
          result: result,
        });
      }
    }
    
    return chartData;
  };
  
  const monthlyChartData = prepareMonthlyChartData();

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  if (isLoading) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-5 w-40 rounded bg-slate-200 mb-4"></div>
            <div className="h-32 rounded-2xl bg-slate-100"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="p-6">
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
            <button type="button" className="ml-3 min-h-[40px] underline" onClick={() => setRetry((value) => value + 1)}>{t("dashboardDesign.retry")}</button>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Ensure values are numbers
  const profit = parseFloat(summary.profit || 0);
  // Convert profit_margin to number (it may come as string from API)
  const profitMargin = typeof summary.profit_margin === 'string' 
    ? parseFloat(summary.profit_margin) 
    : (summary.profit_margin || 0);

  const totalCosts = parseFloat(summary.costs?.total || 0);

  // Break down income
  const customerInvoiceIncome = parseFloat(summary.income?.customer_invoices || 0);
  const totalIncome = parseFloat(summary.income?.total || customerInvoiceIncome);

  // Expected report income data
  const expectedReportIncome = parseFloat(summary.expected_report_income?.income || 0);
  const expectedReportCosts = parseFloat(summary.expected_report_income?.costs || 0);
  const expectedReportProfit = parseFloat(summary.expected_report_income?.profit || 0);

  const financialColor = (value, neutralClass = "text-slate-900") =>
    signedFinancialColorClass(value, { neutralClass });

  const calculatedAtLabel = summary.calculated_at
    ? new Date(summary.calculated_at).toLocaleDateString("nb-NO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <section className={dashboardDesign ? "pd-card" : "rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden"}>
      {dashboardDesign ? <DashboardEconomyView summary={summary} year={selectedYear} chartData={monthlyChartData} formatCurrency={formatCurrency}
        onCosts={() => setCostsDialogOpen(true)} onDetails={() => setSummaryDialogOpen(true)}
        onProjects={() => navigate(`/dashboard/accounting/projects-expected-income?year=${selectedYear}`)} onGuide={openCalcGuide}
        controls={<>
                {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyCalcGuide}
                      disabled={guideBusy}
                      title={t("accounting.summary.calc_guide.json.copy")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {t("accounting.summary.calc_guide.json.copy")}
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadCalcGuide}
                      disabled={guideBusy}
                      title={t("accounting.summary.calc_guide.json.download")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <CodeBracketIcon className="h-4 w-4" />
                      {t("accounting.summary.calc_guide.json.download")}
                    </button>
                  </div>
                )}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <PeriodSelector
                    periodType={period.periodType}
                    from={period.from}
                    to={period.to}
                    allowedPeriodTypes={["year"]}
                    onChange={handlePeriodChange}
                  />
                </div>
            {guideMessage && <p role="status">{guideMessage}</p>}
        </>} /> : <>
      {/* Mobile compact view — tap to open full breakdown */}
      <button
        type="button"
        onClick={() => setSummaryDialogOpen(true)}
        className="md:hidden w-full p-4 text-left transition active:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
            <ChartBarIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("accounting.summary.eyebrow")}
            </div>
            <h3 className="text-base font-semibold leading-tight text-slate-950">
              {t("accounting.summary.title")}
            </h3>
            {calculatedAtLabel && (
              <p className="text-[11px] text-slate-500">{calculatedAtLabel}</p>
            )}
          </div>
          <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t("accounting.summary.profit")}
            </div>
            <div className={`mt-0.5 text-sm font-bold tabular-nums ${financialColor(profit)}`}>
              {formatCurrency(profit)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t("accounting.summary.costs")}
            </div>
            <div className={`mt-0.5 text-sm font-bold tabular-nums ${financialColor(totalCosts)}`}>
              {formatCurrency(totalCosts)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {t("accounting.summary.income")}
            </div>
            <div className={`mt-0.5 text-sm font-bold tabular-nums ${financialColor(totalIncome)}`}>
              {formatCurrency(totalIncome)}
            </div>
          </div>
        </div>

        <p className="mt-2 text-[11px] text-slate-400">
          {t("accounting.summary.tap_hint")}
        </p>
      </button>

      {/* Desktop view */}
      <div className="hidden md:block p-6">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                  <ChartBarIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("accounting.summary.eyebrow")}
                  </div>
                  <h3 className="mt-1 text-xl font-semibold text-slate-950">
                    {t("accounting.summary.title")}
                  </h3>
                  <p className="text-xs text-slate-500">{calculatedAtLabel}</p>
                </div>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyCalcGuide}
                      disabled={guideBusy}
                      title={t("accounting.summary.calc_guide.json.copy")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {t("accounting.summary.calc_guide.json.copy")}
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadCalcGuide}
                      disabled={guideBusy}
                      title={t("accounting.summary.calc_guide.json.download")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <CodeBracketIcon className="h-4 w-4" />
                      {t("accounting.summary.calc_guide.json.download")}
                    </button>
                  </div>
                )}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <PeriodSelector
                    periodType={period.periodType}
                    from={period.from}
                    to={period.to}
                    allowedPeriodTypes={["year"]}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
            </div>
            {guideMessage && (
              <p className="mb-4 text-xs text-slate-600">{guideMessage}</p>
            )}

            {/* Summary Numbers and Chart */}
            <div className="mb-6">
              <div className="flex items-start gap-6">
                {/* Chart on the left */}
                <div className="flex-1">
                  {/* Monthly Chart */}
                  {monthlyChartData && monthlyChartData.length > 0 ? (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-slate-900">
                          {t("accounting.summary.monthly_overview")}
                        </h4>
                        <p className="mt-1 text-xs text-slate-500">
                          {t("accounting.summary.chart_description")}
                        </p>
                      </div>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={monthlyChartData}
                            margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fill: '#64748b', fontSize: 12 }}
                              stroke="#9ca3af"
                            />
                            <YAxis 
                              tick={{ fill: '#64748b', fontSize: 12 }}
                              stroke="#9ca3af"
                              tickFormatter={(value) => {
                                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                                return value.toString();
                              }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#f3f4f6',
                              }}
                              formatter={(value, name) => {
                                if (typeof value === "number") {
                                  const formatted = formatCurrency(value);
                                  const labelMap = {
                                    income: t("accounting.summary.income"),
                                    cost: t("accounting.summary.costs"),
                                    result: t("accounting.summary.profit"),
                                  };
                                  return [formatted, labelMap[name] || name];
                                }
                                return [value, name];
                              }}
                              labelStyle={{ color: '#f3f4f6', fontWeight: 'bold', marginBottom: '4px' }}
                            />
                            <Legend 
                              wrapperStyle={{ paddingTop: '20px' }}
                              iconType="rect"
                            />
                            {/* Income bar */}
                            <Bar 
                              dataKey="income" 
                              name={t("accounting.summary.income")}
                              fill="#10b981" 
                              radius={[4, 4, 0, 0]}
                              barSize={18}
                            />
                            {/* Cost bar */}
                            <Bar 
                              dataKey="cost" 
                              name={t("accounting.summary.costs")}
                              fill="#ef4444" 
                              radius={[4, 4, 0, 0]}
                              barSize={18}
                            />
                            {/* Result line (income - cost) */}
                            <Line
                              type="monotone"
                              dataKey="result"
                              name={t("accounting.summary.profit")}
                              stroke="#8b5cf6"
                              strokeWidth={2}
                              dot={{ fill: '#8b5cf6', r: 4 }}
                              activeDot={{ r: 6, fill: '#7c3aed' }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 py-12 text-center text-slate-500">
                      <p className="text-sm">{t("accounting.summary.no_monthly_data")}</p>
                    </div>
                  )}
                </div>

                {/* Numbers on the right */}
                <div className="flex flex-col gap-4 min-w-[220px]">
                  {/* Profit */}
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-950 p-4 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      {profit >= 0 ? (
                        <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-300" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-5 w-5 text-red-300" />
                      )}
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                        {t("accounting.summary.profit")}
                      </span>
                      <CalculationInfoIcon
                        className="ml-auto text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                        ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.profit.ariaKey)}
                        onClick={() => openCalcGuide("profit")}
                      />
                    </div>
                    <div
                      className={`text-2xl font-bold mb-1 ${
                        profit > 0
                          ? "text-emerald-300"
                          : profit < 0
                            ? "text-red-300"
                            : "text-white"
                      }`}
                    >
                      {formatCurrency(profit)}
                    </div>
                    <div
                      className={`text-xs ${
                        profitMargin > 0
                          ? "text-emerald-300"
                          : profitMargin < 0
                            ? "text-red-300"
                            : "text-slate-300"
                      }`}
                    >
                      {profitMargin >= 0 ? "+" : ""}
                      {profitMargin.toFixed(1)}% {t("accounting.summary.profit_margin")}
                    </div>
                  </div>

                  {/* Costs total — click for breakdown (div+role to avoid nested buttons with info icon) */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setCostsDialogOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setCostsDialogOpen(true);
                      }
                    }}
                    className="w-full cursor-pointer rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-slate-300 hover:bg-slate-100/80 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {t("accounting.summary.costs")}
                        <CalculationInfoIcon
                          ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.costs.ariaKey)}
                          onClick={() => openCalcGuide("costs")}
                        />
                      </div>
                      <span className={`text-sm font-bold ${financialColor(totalCosts)}`}>
                        {formatCurrency(totalCosts)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {t("accounting.summary.costs_detail_hint")}
                    </p>
                  </div>

                  {/* Income Breakdown */}
                  <div className="space-y-2 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {t("accounting.summary.income")}
                        <CalculationInfoIcon
                          ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.income.ariaKey)}
                          onClick={() => openCalcGuide("income")}
                        />
                      </div>
                      <span className={`text-sm font-bold ${financialColor(totalIncome)}`}>
                        {formatCurrency(totalIncome)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">
                        {t("accounting.summary.customer_invoices")}
                      </span>
                      <span className={`font-medium ${financialColor(customerInvoiceIncome)}`}>
                        {formatCurrency(customerInvoiceIncome)}
                      </span>
                    </div>
                  </div>

                  {/* Expected Report Income */}
                  {(expectedReportIncome > 0 || expectedReportCosts > 0) && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/dashboard/accounting/projects-expected-income?year=${selectedYear}`)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(
                            `/dashboard/accounting/projects-expected-income?year=${selectedYear}`
                          );
                        }
                      }}
                      className="w-full cursor-pointer rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-slate-300 hover:bg-slate-100/80 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <ChartBarIcon className="h-5 w-5 text-slate-700" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {t("accounting.summary.expected_report_income")}
                        </span>
                        <CalculationInfoIcon
                          ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.expected_report_income.ariaKey)}
                          onClick={() => openCalcGuide("expected_report_income")}
                        />
                        <span className="ml-auto text-xs text-slate-500">
                          {t("accounting.summary.view_projects")}
                        </span>
                      </div>
                      <p className="mb-3 text-xs text-slate-500">
                        {t("accounting.summary.expected_report_income_hint")}
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">
                            {t("accounting.summary.expected_income")}
                          </span>
                          <span className={`font-medium ${financialColor(expectedReportIncome)}`}>
                            {formatCurrency(expectedReportIncome)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">
                            {t("accounting.summary.expected_costs")}
                          </span>
                          <span className={`font-medium ${financialColor(expectedReportCosts)}`}>
                            {formatCurrency(expectedReportCosts)}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-slate-200">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-slate-700">
                              {t("accounting.summary.expected_profit")}
                            </span>
                            <span
                              className={`text-sm font-bold ${financialColor(expectedReportProfit)}`}
                            >
                              {formatCurrency(expectedReportProfit)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
      </div>
      </>}

      <CostsDetailDialog
        open={costsDialogOpen}
        onClose={() => setCostsDialogOpen(false)}
        costs={summary.costs}
        formatCurrency={formatCurrency}
        year={selectedYear}
        onOpenCalcGuide={openCalcGuide}
      />

      <AccountingSummaryDetailDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        summary={summary}
        formatCurrency={formatCurrency}
        selectedYear={selectedYear}
        period={period}
        onPeriodChange={handlePeriodChange}
        onNavigate={navigate}
        onOpenCalcGuide={openCalcGuide}
      />

      <CalculationGuideDialog
        open={Boolean(calcGuideKey)}
        onClose={() => setCalcGuideKey(null)}
        titleKey={
          calcGuideKey ? SUMMARY_CALC_GUIDE_KEYS[calcGuideKey]?.titleKey : undefined
        }
        bodyKey={
          calcGuideKey ? SUMMARY_CALC_GUIDE_KEYS[calcGuideKey]?.bodyKey : undefined
        }
      />
    </section>
  );
};

